import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Lock, BookOpen, Brain, Target, TrendingUp, Landmark, Globe,
  Lightbulb, Building2, Swords, Clock, BarChart3, Layers
} from "lucide-react";

/** Module metadata keyed by route slug */
const MODULE_META: Record<string, { moduleKey: string; title: string; icon: React.ElementType; intro: string; concepts: { title: string; body: string; icon: React.ElementType }[] }> = {
  "resource-allocation": {
    moduleKey: "resource_allocation", title: "Resource Allocation Simulator", icon: Layers,
    intro: "Manage limited resources across competing priorities. Learn how scarcity, tradeoffs, and marginal thinking shape every allocation decision.",
    concepts: [
      { title: "Scarcity Forces Choices", body: "Resources are limited — every allocation to one priority means less for another. Understanding scarcity is the foundation of economic reasoning.", icon: Target },
      { title: "Opportunity Cost", body: "The true cost of any decision is what you give up. Evaluating opportunity costs helps you allocate resources where they create the most value.", icon: TrendingUp },
      { title: "Marginal Thinking", body: "Smart allocation means comparing the additional benefit vs. additional cost of each incremental unit — not all-or-nothing decisions.", icon: Brain },
    ],
  },
  "market-dynamics": {
    moduleKey: "market_dynamics", title: "Market Dynamics Simulator", icon: BarChart3,
    intro: "Manipulate supply, demand, taxes, and price controls to visualize how markets reach equilibrium — and what happens when they don't.",
    concepts: [
      { title: "Supply & Demand", body: "Markets coordinate through prices. When demand exceeds supply, prices rise; when supply exceeds demand, prices fall.", icon: TrendingUp },
      { title: "Price Controls", body: "Price ceilings create shortages; price floors create surpluses. Well-intentioned interventions can produce unintended consequences.", icon: Target },
      { title: "Welfare Loss", body: "Taxes, subsidies, and regulations can create deadweight loss — economic value destroyed by market distortions.", icon: Brain },
    ],
  },
  "industry-competition": {
    moduleKey: "industry_competition", title: "Industry Competition Simulator", icon: Swords,
    intro: "Compete in monopoly, oligopoly, and perfectly competitive environments. Understand pricing power, barriers to entry, and strategic rivalry.",
    concepts: [
      { title: "Market Power", body: "Fewer competitors means more pricing power. Monopolists set prices; perfect competitors accept them.", icon: Target },
      { title: "Barriers to Entry", body: "Patents, scale, and capital requirements protect incumbent firms. Understanding barriers explains why some industries stay concentrated.", icon: Lock },
      { title: "Strategic Rivalry", body: "In oligopolies, every decision affects your rivals. Price wars, signaling, and coordination shape competitive outcomes.", icon: Swords },
    ],
  },
  "career-market": {
    moduleKey: "career_market", title: "Career Market Simulator", icon: TrendingUp,
    intro: "Navigate specialization, relocation, and industry-switching decisions. Simulate how labor demand shifts and wage dynamics affect your career trajectory.",
    concepts: [
      { title: "Human Capital", body: "Your skills and knowledge are your most valuable asset. Investing in human capital drives long-term earning potential.", icon: Brain },
      { title: "Labor Market Signals", body: "Degrees, certifications, and experience signal ability to employers. Understanding what signals matter helps you invest wisely.", icon: Target },
      { title: "Geographic Mobility", body: "Location affects opportunity. Willingness to relocate — physically or digitally — expands your labor market options.", icon: Globe },
    ],
  },
  "macro-dashboard": {
    moduleKey: "macro_dashboard", title: "Macro Dashboard Simulator", icon: BarChart3,
    intro: "Interpret economic indicators to identify recessions, expansions, inflation pressure, and growth conditions.",
    concepts: [
      { title: "GDP & Growth", body: "GDP measures total economic output. Growth rate changes signal expansion or contraction.", icon: TrendingUp },
      { title: "Inflation Signals", body: "Rising prices erode purchasing power. Understanding what drives inflation helps you interpret policy responses.", icon: Target },
      { title: "Leading Indicators", body: "Some data predicts the future; other data confirms the past. Knowing the difference prevents panic.", icon: Brain },
    ],
  },
  "banking-system": {
    moduleKey: "banking_system", title: "Banking System Simulator", icon: Landmark,
    intro: "Simulate bank lending, reserve pressure, liquidity needs, and confidence shocks.",
    concepts: [
      { title: "Fractional Reserves", body: "Banks lend out most of their deposits, keeping only a fraction as reserves. This creates money — but also vulnerability.", icon: Landmark },
      { title: "Central Bank Tools", body: "Interest rates and open market operations are how central banks steer the economy. Every tool has tradeoffs.", icon: Target },
      { title: "Confidence & Stability", body: "Banking depends on trust. When confidence breaks, bank runs and credit freezes cascade through the economy.", icon: Brain },
    ],
  },
  "policy-tradeoff": {
    moduleKey: "policy_tradeoff", title: "Policy Tradeoff Simulator", icon: Landmark,
    intro: "Balance tax policy, transfers, infrastructure, regulation, and stabilization measures.",
    concepts: [
      { title: "Fiscal Tradeoffs", body: "More spending means more debt or higher taxes. Every government dollar has an opportunity cost.", icon: Target },
      { title: "Automatic Stabilizers", body: "Programs like unemployment insurance automatically cushion downturns without new legislation.", icon: TrendingUp },
      { title: "Crowding Out", body: "Government borrowing can compete with private investment for capital.", icon: Brain },
    ],
  },
  "global-trade": {
    moduleKey: "global_trade", title: "Global Trade Simulator", icon: Globe,
    intro: "Simulate tariffs, supply chain disruptions, exchange rate shifts, and trade partnerships.",
    concepts: [
      { title: "Comparative Advantage", body: "Countries trade because specialization makes everyone better off.", icon: TrendingUp },
      { title: "Exchange Rates", body: "Currency values affect trade competitiveness. A weaker currency boosts exports but raises import costs.", icon: Target },
      { title: "Supply Chain Risk", body: "Global supply chains optimize efficiency but create fragility.", icon: Brain },
    ],
  },
  "choice-architecture": {
    moduleKey: "choice_architecture", title: "Choice Architecture Lab", icon: Lightbulb,
    intro: "Design choice environments that influence decisions through framing, defaults, and nudges.",
    concepts: [
      { title: "Default Power", body: "Most people stick with the default. Whoever designs the default shapes the outcome.", icon: Target },
      { title: "Framing Effects", body: "The same information presented differently leads to different choices.", icon: Brain },
      { title: "Nudges vs. Mandates", body: "Nudges preserve choice while guiding behavior. The right tool depends on the stakes.", icon: Lightbulb },
    ],
  },
  "nation-builder": {
    moduleKey: "nation_builder", title: "Nation Builder Simulator", icon: Building2,
    intro: "Allocate resources and reform institutions over time to drive national development.",
    concepts: [
      { title: "Institutions Matter", body: "Property rights, rule of law, and contract enforcement create the trust needed for markets to function.", icon: Landmark },
      { title: "Resource Curse", body: "Natural wealth without strong institutions often leads to corruption rather than prosperity.", icon: Target },
      { title: "Human Capital Investment", body: "Education and health spending compound over decades, building the foundation for sustained growth.", icon: Brain },
    ],
  },
  "strategy-arena": {
    moduleKey: "strategy_arena", title: "Strategy Arena", icon: Swords,
    intro: "Make repeated strategic decisions involving signaling, cooperation, retaliation, and commitment.",
    concepts: [
      { title: "Strategic Interdependence", body: "Your best move depends on what others do. Game theory provides frameworks for interdependent choices.", icon: Target },
      { title: "Cooperation & Trust", body: "In repeated interactions, building trust through cooperation often beats short-term defection.", icon: Brain },
      { title: "Credible Commitments", body: "Making it costly to break your promises makes them believable — and changes how others respond.", icon: Lock },
    ],
  },
  "crisis-timeline": {
    moduleKey: "crisis_timeline", title: "Crisis Timeline Simulator", icon: Clock,
    intro: "Walk through major economic crises and make the interpretation and response choices that shaped outcomes.",
    concepts: [
      { title: "Pattern Recognition", body: "Economic crises follow similar patterns: excess, denial, panic, policy response.", icon: Brain },
      { title: "Policy Mistakes", body: "Bad policy often deepens crises. Tightening money during a downturn makes everything worse.", icon: Target },
      { title: "Human Psychology", body: "Overconfidence during booms and panic during busts drive crises.", icon: Lightbulb },
    ],
  },
};

type Step = "intro" | "concept_1" | "concept_2" | "concept_3" | "quiz" | "summary";
const STEPS: Step[] = ["intro", "concept_1", "concept_2", "concept_3", "quiz", "summary"];

const EconomicsModule = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { moduleGates, loading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const [step, setStep] = useState<Step>("intro");
  const [completed, setCompleted] = useState(false);

  const meta = slug ? MODULE_META[slug] : undefined;
  if (!meta) return <div className="p-8 text-center text-muted-foreground">Module not found.</div>;

  const gate = moduleGates.find(g => g.moduleKey === meta.moduleKey);
  const isLocked = !gate?.isUnlocked;
  const isCompleted = gate?.isCompleted || completed;
  if (loading) return null;

  return (
    <ModuleShell
      title={meta.title}
      stepIndex={STEPS.indexOf(step)}
      totalSteps={STEPS.length}
      isLocked={isLocked}
      lockMessage={gate?.unlockRequirement || "Complete the prerequisite course to unlock."}
      isCompleted={isCompleted && step === "intro"}
      completedMessage="You've completed this module!"
      completedAction={<Button variant="outline" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>}
      stepKey={step}
    >
      {/* Intro */}
      {step === "intro" && !isLocked && (
        <ModuleStepCard
          title={meta.title}
          icon={<meta.icon className="w-8 h-8 text-primary" />}
          body={meta.intro}
          highlights={meta.concepts.map(c => c.title)}
          action={<Button className="w-full" onClick={() => setStep("concept_1")}>Begin <ArrowRight className="ml-2 w-4 h-4" /></Button>}
        />
      )}

      {/* Concept steps */}
      {["concept_1", "concept_2", "concept_3"].includes(step) && (() => {
        const idx = parseInt(step.split("_")[1]) - 1;
        const concept = meta.concepts[idx];
        if (!concept) return null;
        const ConceptIcon = concept.icon;
        const nextStep = idx < 2 ? `concept_${idx + 2}` as Step : "quiz";
        return (
          <ModuleStepCard
            title={concept.title}
            icon={<ConceptIcon className="w-7 h-7 text-primary" />}
            body={concept.body}
            action={<Button className="w-full" onClick={() => setStep(nextStep)}>Continue <ArrowRight className="ml-2 w-4 h-4" /></Button>}
          />
        );
      })()}

      {/* Quiz */}
      {step === "quiz" && (
        <ModuleQuiz
          moduleKey={meta.moduleKey}
          onPass={async () => {
            await completeModule(meta.moduleKey);
            setCompleted(true);
            setStep("summary");
          }}
        />
      )}

      {/* Summary */}
      {step === "summary" && (
        <ModuleStepCard
          title={completed ? "Module Complete! 🎓" : "Not Quite Yet"}
          icon={<BookOpen className="w-7 h-7 text-primary" />}
          body={completed
            ? "You've demonstrated understanding of the core concepts. These ideas will appear throughout your economics journey."
            : "You need 80% (4/5) to pass. Review the concepts and try again."}
          action={completed
            ? <Button className="w-full" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
            : <Button className="w-full" onClick={() => setStep("concept_1")}>Review & Retry</Button>}
        />
      )}
    </ModuleShell>
  );
};

export default EconomicsModule;